import axios from "axios";
import { baseClient, baseUrl, getToken } from "./baseRequest";
import {
  ClocksListResponse,
  Device,
  ClockErrorsResponse,
  ClockReminderSummaryEntry,
} from "../Types";
import {
  FieldChangeFilter,
  FieldChangeRow,
} from "../views/Device/hooks/useClockChangeLogs";
import { PopulatedGift } from "./giftCategories";

export type MapBounds = {
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
};

export type TransferDeviceDataSummary = {
  machineDoc: boolean;
  machineReport: boolean;
  giftsUpdated: number;
  collectionCodesUpdated: number;
  collectionCodeRedemptionsUpdated: number;
};

export type TransferDeviceDataParams = {
  targetClockId: string;
  overwrite?: boolean;
  // Defaults to true. Machine doc (except collections), report and gifts.
  transferData?: boolean;
  // Defaults to false. Copies the data instead of moving it, so the source
  // keeps it. Never applies to collections.
  keepSourceData?: boolean;
  // Defaults to true. Moves the collections and their codes to the target.
  transferCollections?: boolean;
};

export type TransferDeviceDataResult =
  | {
      status: "confirmation_required";
      targetHasData: true;
      targetIdf?: string;
    }
  | {
      status: "success";
      summary: TransferDeviceDataSummary;
    };

export type MapDevice = {
  id: string;
  loopUrl: string;
  location: {
    latitude: number;
    longitude: number;
  };
  randomLocation?: {
    latitude: number;
    longitude: number;
  };
};

export type MapCollection = {
  id: string;
  collectionId: string;
  coverUrl: string;
  location: {
    latitude: number;
    longitude: number;
  };
  randomLocation?: {
    latitude: number;
    longitude: number;
  };
};

export type TopCollectionStats = {
  collectionId: string;
  name: string;
  coverUrl: string;
  usageCount: number;
};

export type TopCollectionsResponse = {
  collections: TopCollectionStats[];
  totalCount: number;
};

export type TopKlydoStats = {
  klydoId: string;
  name: string;
  loopUrl: string;
  usageCount: number;
};

export type TopKlydosResponse = {
  klydos: TopKlydoStats[];
  totalCount: number;
};

export type VersionUpdate = {
  version: number;
  update: "public" | "beta" | "obsolete" | "publicOldPlayer" | "artistVersion";
};

export enum UserType {
  USER = "User",
  ARTIST = "Artist",
}

export type MonthlyRegisteredDevicesResponse = {
  firmware_version: string;
  month_registered: number;
  amount: number;
};

export type GreetCategoryStats = {
  category: string;
  greetCount: number;
};

export type GreetCategoryKlydoStats = {
  klydoId: string;
  greetCount: number;
};

export enum CuratorLevel {
  KLYDO_CURATOR = "klydo_curator",
  SENIOR_CURATOR = "senior_curator",
  MASTER_CURATOR = "master_curator",
}

export type UserProperties = {
  b2b: boolean;
  curatorLevel?: CuratorLevel;
};

export type User = {
  name: string;
  email: string;
  id: string;
  photoUrl?: string;
  animated?: boolean;
  tag?: string;
  bio?: string;
  state?: string;
  description?: string;
  featured?: boolean;
  curatorLevel?: CuratorLevel | null;
  githubLogin?: string;
  birthday?: Date;
  country?: string;
  studioContract: {
    accepted: boolean;
    signedTimestamp: number | null;
  };
  type: UserType;
  socialMedia?: {
    link: string;
    network: string;
  }[];
  manualPayout?: boolean;
  metadata?: {
    collaboratorId?: string;
    defaultPlanId?: string | null;
    isArtistPageHidden?: boolean;
    ignoreAffiliatePayments?: boolean;
  };
};
export type KlydoAnalyzerConfig = {
  prompt: string;
  examples: string[];
};

export type GetClocksInput = {
  skip: number;
  limit: number;
  query: string;
  devClocks?: boolean;
  betaClocks?: boolean;
  registrationStatus?: "ALL" | "NOT_REGISTERED" | "REGISTERED";
  fromVersion?: number;
  toVersion?: number;
  heartbeatMinutes?: number;
  artistClocks?: boolean;
  country?: string;
  city?: string;
  region?: string;
  timeZone?: string;
  mode?: string;
  cycle?: string;
  // Synced-setting filters, `field:value` / `field:min~max` (see syncedSettings.ts).
  settings?: string[];
  ids?: string[];
  monitorId?: string;
  featureBetaGroupId?: string;
  fetchAll?: boolean;
  sortBy?: ClockSortField;
  sortDir?: ClockSortDirection;
};

// Sortable Devices-table columns. The API rejects anything else — keep in sync
// with CLOCK_SORT_FIELDS in klydo-api Admin.service.ts.
export const CLOCK_SORT_FIELDS = [
  "idf",
  "location",
  "heartbeat",
  "currentIdf",
  "registerProduct",
] as const;

export type ClockSortField = (typeof CLOCK_SORT_FIELDS)[number];
export type ClockSortDirection = "asc" | "desc";

export type ClockLocationEntry = {
  country_code: string | null;
  city: string | null;
  region: string | null;
  timeZone: string | null;
};

export type ClockLocations = ClockLocationEntry[];

export type GetClocksErrorsInput = {
  fromVersion?: number | null;
  toVersion?: number | null;
  skip?: number;
  limit?: number;
};

export type SearchUserInput = {
  query: string;
  options: { limit: number; skip: number };
  searchFields: string[];
};

export type SearchUserOutput = {
  id: string;
  name: string;
};

export enum ClockOnlineStatus {
  ONLINE = "Online",
  OFFLINE = "Offline",
}

export type ClockOnlineLog = {
  status: ClockOnlineStatus;
  period_start: string;
  period_end: string;
};

export enum DeviceLogSeverity {
  ERROR = "ERROR",
  CRITICAL = "CRITICAL",
  ALL = "ALL",
}

export type DeviceErrorLog = {
  timestamp: string;
  severity: string;
  log: string;
  exception?: string;
  version?: number;
};

export type ClockMetadata = {
  tags?: string[];
};

export type ClockErrorCount = {
  clockId: string;
  count: number;
};

export type GroupedClockError = {
  log: string;
  errorMessage: string;
  method: string;
  class: string;
  clocks: ClockErrorCount[];
  occurrences: number;
  lastOccurrence: string;
  isDeleted: boolean;
};

export type IgnoredClockError = {
  _id: string;
  errorMessage: string;
  method: string;
  class: string;
  version: number;
  clockId: string | null;
  createdBy: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type KlydoStatsResponse = {
  totalFavorites: number;
  totalViewingTime: number;
  topFavoritedKlydos: {
    id: string;
    name: string;
    loopUrl: string;
    favorites: number;
  }[];
  topViewedKlydos: {
    id: string;
    name: string;
    loopUrl: string;
    viewed: number;
  }[];
  topDeletedKlydos: {
    id: string;
    name: string;
    loopUrl: string;
    deleted: number;
  }[];
};

export type ClockSettingsDistributionResponse = Record<
  string,
  { value: string; count: number }[]
>;

/**
 * ECO-mode adoption across the clocks that can actually run it.
 *
 * ECO shipped in clock version 624.3, so the counts cover clocks at or above
 * `fromVersion` only - older clocks have no ECO code and would otherwise be counted
 * as "on" by the setting's default.
 */
export type EcoModeStatsResponse = {
  fromVersion: number;
  /** ECO-capable clocks - the denominator for the three counts below. */
  total: number;
  /** Clocks with the ECO toggle on (includes those still on the on-by-default value). */
  enabled: number;
  /** Clocks that explicitly turned the ECO toggle off. */
  disabled: number;
  /** Clocks whose screen is in the ECO cycle right now. */
  inEcoNow: number;
  /**
   * False until at least one clock has written `ecoModeEnabled` - the field only
   * reaches the backend when a user toggles it, so before the first toggle every
   * clock sits on the on-by-default value and `disabled` is necessarily 0.
   */
  settingReported: boolean;
};

// Per-menu left/right navigation counts, keyed by menuId (e.g. "info", "main").
export type HeartLongPressStatsResponse = {
  clocks: number;
  presses: number;
  avgPerClock: number;
};

export type MenuNavigationDistributionResponse = Record<
  string,
  { direction: string; count: number }[]
>;

export type ExploreClocksCountResponse = { numberOfClocks: number };

export type KlydoFeedStats = {
  klydosPublished: number;
  klydosReachedFeed: number;
  reachedFeedPercentage: number;
  avgDaysToFeed: number | null;
};

export type AddCollectionQrStats = {
  scans: number;
  uniqueClocks: number;
  clocksThatPaid: number;
};

export type ExploreFavoriteStats = {
  subModeId: string;
  favorited: number;
  unfavorited: number;
};

export type ArtistsLandingPageStats = {
  totalBecomeArtistClicks: number;
  googleSignInClicks: number;
  joinNowClicks: number;
  noFollowUpClicks: number;
  googleSignInPercentage: number;
  joinNowPercentage: number;
  noFollowUpPercentage: number;
  onboardingStarted: number;
  onboardingSkipped: number;
  onboardingCompleted: number;
  avgOnboardingSkipSlide: number | null;
  studioContractSigned: number;
  dashboardFirstVisit: number;
  klydoUploadStarted: number;
  klydoPublished: number;
  collectionCreated: number;
  collectionPublished: number;
};

export type CollectionProductPageEngagementStats = {
  totalClicks: number;
  uniqueArtists: number;
};

export type AffiliateClockStat = {
  collectionId: string;
  name: string;
  artistName: string;
  editionOpened: number;
  editionCopied: number;
  editionViewed: number;
  totalClicks: number;
  productViews: number;
  purchases: number;
  conversionRate: number;
  // Public artist-edition PDP URL resolved from the real Shopify handle.
  // Absent when the backend couldn't fetch it; the UI then derives one from
  // the collection name.
  productUrl?: string;
  type: "artist" | "backoffice";
};

export type AffiliateClockStats = {
  collections: AffiliateClockStat[];
};

export type KlydoGreetOrderStats = {
  klydoGreetOrders: number;
  clockOrders: number;
  // Share of clock orders that added a KlydoGreet, 0-100.
  attachRate: number;
  // Orders whose greet was actually written, with or without KlydoGreet.
  greetOrders: number;
  // KlydoGreet orders that went on to write their greet.
  klydoGreetOrdersWithGreet: number;
  // Share of KlydoGreet orders that wrote their greet, 0-100.
  greetCompletionRate: number;
};

export type AiGreetStats = {
  total: number;
  daily: { date: string; count: number }[];
  byTone: { tone: string; count: number }[];
};

export type ArtistPreviewStats = {
  totalClicks: number;
  avgClicksPerUser: number;
  avgClicksPerKlydo: number;
};

export type SentToClockArtistStat = {
  userId: string;
  artistName: string;
  klydoSends: number;
  collectionSends: number;
  /** ISO date of this artist's most recent send in the range; '' if unknown. */
  lastSentAt: string;
};

export type SentToClockStats = {
  totalSends: number;
  uniqueArtists: number;
  klydoSends: number;
  collectionSends: number;
  artists: SentToClockArtistStat[];
};

export type CollectionAnalyticsResponse = {
  totalDevices: number;
  devicesWithCollections: number;
  percentage: number;
  distribution: { bucket: string; count: number; percentage: number }[];
  firstCollectionLast24h: number;
};

export type CollectionPurchaseStatsEntry = {
  /** Purchase month as 'YYYY-MM'. */
  month: string;
  totalPurchases: number;
  /** Collection-sale income for the month, in USD. */
  totalRevenue: number;
  /** `totalRevenue` divided by the clocks registered by the month's end. */
  revenuePerClock: number;
};

export type CollectionPurchaseStatsResponse = CollectionPurchaseStatsEntry[];

export type GitHubPull = {
  message: string;
  date: string;
  url: string;
};

export type FaqLink = {
  text: string;
  url: string;
};

export type FaqItem = {
  question: string;
  answer: string;
  links?: FaqLink[];
};

export type GuideDisplayMode = "sections" | "slides";

export type GuideBullet = {
  lead?: string;
  text: string;
};

export type GuideLink = {
  text: string;
  url: string;
};

export type GuideSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: GuideBullet[];
  // Links embedded in the section body text (paragraphs + bullets), matched by
  // `text`. A URL pointing at the public klydo route opens a klydo preview
  // popup in the reader instead of a new tab.
  links?: GuideLink[];
  note?: string;
  noteLinks?: GuideLink[];
};

export type StudioGuide = {
  id: string;
  title: string;
  subtitle: string;
  icon?: string;
  displayMode: GuideDisplayMode;
  sections?: GuideSection[];
};

export type UserCollectionInfo = {
  id: string;
  name: string;
  klydoCount: number;
  shopifyProductId: string | null;
  isPublishedToShopify: boolean;
};

export type UserKlydoInfo = {
  id: string;
  name: string;
  collectionId: string | null;
  collectionName: string | null;
};

export type ArtistMetaobjectInfo = {
  id: string;
  name: string | null;
};

export type UserContent = {
  collections: UserCollectionInfo[];
  klydos: UserKlydoInfo[];
  artistMetaobject: ArtistMetaobjectInfo | null;
  collaboratorId: string | null;
};

export type DeleteUserOptions = {
  klydoIds?: string[];
  collectionIds?: string[];
  deleteArtistMetaobject?: boolean;
};

// NOTE: this contract mirrors klydo-api DeleteUserService.ts (UserContent /
// DeleteUserOptions / DeleteUserResult). Keep the definitions in sync.
export type DeleteUserResultItem = {
  type:
    | "klydo"
    | "collection"
    | "collectionReferences"
    | "artistMetaobject"
    | "userProperties"
    | "authUser"
    | "userDocument"
    | "firestoreUser";
  id: string;
};

export type DeleteUserResult = {
  deleted: DeleteUserResultItem[];
  failed: (DeleteUserResultItem & { error: string })[];
  // The CollabPay collaborator, which is NOT deleted (null if the user had none).
  collaboratorId: string | null;
};

export type AdminApiClient = {
  saveOnboardingImages: (images: string[]) => Promise<void>;
  getOnboardingImages: () => Promise<{ images: string[] }>;
  getStudioCommits: (repo: string, since?: string) => Promise<GitHubPull[]>;
  getStudioContract: () => Promise<{ html: string }>;
  saveStudioContract: (html: string) => Promise<void>;
  getStudioFaq: () => Promise<{ items: FaqItem[] }>;
  saveStudioFaq: (items: FaqItem[]) => Promise<void>;
  getStudioGuides: () => Promise<{ items: StudioGuide[] }>;
  saveStudioGuides: (items: StudioGuide[]) => Promise<void>;
  publishVersion: (versionUpdate: VersionUpdate) => Promise<void>;
  getStudioUsersStats: () => Promise<{
    total: number;
    daily: { date: string; count: number }[];
  }>;
  getNumberOfArtists: () => Promise<number>;
  getNumberOfArtistsCreatedKlydos: () => Promise<number>;
  getNumberOfArtistsClocks: () => Promise<number>;
  getNumberOfPendingPublicationRequests: () => Promise<number>;
  getNumberOfCollectionsInReview: () => Promise<number>;
  getNumberOfCollectionsInMarketplace: () => Promise<number>;
  getNumberOfCollectionsWaitingForPublish: () => Promise<number>;
  getUnregisteredDevicesCount: () => Promise<{ count: number }>;
  getOrderClock: (orderName: string) => Promise<Device[]>;
  updateClockMetadata: (
    clockId: string,
    metadata: Partial<ClockMetadata>,
  ) => Promise<ClockMetadata>;
  getClockMetadata: (clockId: string) => Promise<ClockMetadata | null>;
  getAllClocksTags: () => Promise<string[]>;
  getClockLocations: () => Promise<ClockLocations>;
  getClocksByTags: (
    tags: string[],
    includeNonRegistered?: boolean,
    dev?: boolean,
  ) => Promise<Device[]>;
  updateUser: (
    userId: string,
    user: Partial<User> & { role?: string },
  ) => Promise<User>;
  deleteUser: (
    userId: string,
    options?: DeleteUserOptions,
  ) => Promise<DeleteUserResult>;
  getUserContent: (userId: string) => Promise<UserContent>;
  createUser: (
    user: Partial<User> & {
      password: string;
      role: string;
      createCollaborator?: boolean;
    },
  ) => Promise<User>;
  createUserCollaborator: (
    userId: string,
  ) => Promise<{ collaboratorId: string }>;
  resetPassword: (userId: string) => Promise<{ link: string }>;
  setUserPassword: (userId: string, password: string) => Promise<void>;
  setUserEmailVerified: (userId: string) => Promise<void>;
  getClocks: (input: GetClocksInput) => Promise<ClocksListResponse[]>;
  getClock: (id: string) => Promise<Device & ClockMetadata>;
  getClocksRemindersSummary: () => Promise<ClockReminderSummaryEntry[]>;
  getClocksErrors: (
    input: GetClocksErrorsInput,
  ) => Promise<ClockErrorsResponse>;

  getMonthlyRegisteredDevices: (
    monthLimit: number,
  ) => Promise<MonthlyRegisteredDevicesResponse[]>;
  searchUsers: (input: SearchUserInput) => Promise<SearchUserOutput[]>;
  getUserById: (userId: string) => Promise<User>;
  getUserProperties: (userId: string) => Promise<UserProperties>;
  setUserProperties: (
    userId: string,
    b2b: boolean,
  ) => Promise<{ b2b: boolean }>;
  getKlydoShareKey: (klydoId: string) => Promise<{ id: string }>;
  getCollectionShareKey: (collectionId: string) => Promise<{ id: string }>;
  getGreetCategoriesStats: () => Promise<GreetCategoryStats[]>;
  getGreetCategoryKlydoStats: (
    category: string,
  ) => Promise<GreetCategoryKlydoStats[]>;
  getClockChangeLogs: (
    clockId: string,
    limit: number,
    fields: FieldChangeFilter,
    fromDate?: string,
  ) => Promise<FieldChangeRow[]>;
  getGroupedClockErrors: () => Promise<GroupedClockError[]>;
  deleteClockError: (errorGroup: {
    errorMessage: string;
    method: string;
    class: string;
  }) => Promise<void>;
  getMapDevices: (bounds: MapBounds) => Promise<MapDevice[]>;
  getMapCollections: (bounds: MapBounds) => Promise<MapCollection[]>;
  getTopCollections: (
    bounds: MapBounds,
    limit: number,
  ) => Promise<TopCollectionsResponse>;
  getTopKlydos: (
    bounds: MapBounds,
    limit: number,
  ) => Promise<TopKlydosResponse>;
  addClocksToUser: (
    userId: string,
    clockIds: string[],
  ) => Promise<{ user: User; token: string }>;
  getUserClocks: (userId: string) => Promise<string[]>;
  removeClocksFromUser: (
    userId: string,
    clockId: string,
  ) => Promise<{ user: User; token: string }>;
  getLastVersionNumber: (clockId: string) => Promise<number | string>;
  signOutUser: (userId: string) => Promise<void>;
  getClockOnlineLogs: (
    clockId: string,
    dateLimit: string,
  ) => Promise<ClockOnlineLog[]>;
  getDeviceErrorLogs: (
    clockId: string,
    period: string,
    severity: DeviceLogSeverity,
  ) => Promise<DeviceErrorLog[]>;
  clearGift: (clockId: string) => Promise<void>;
  clearOrder: (clockId: string) => Promise<void>;
  detachOrder: (clockId: string, orderName: string) => Promise<void>;
  transferDeviceData: (
    clockId: string,
    params: TransferDeviceDataParams,
  ) => Promise<TransferDeviceDataResult>;
  getKlydoStats: () => Promise<KlydoStatsResponse>;
  getKlydoDeleteCount: (klydoId: string) => Promise<{ deleted: number }>;
  getClockSettingsDistribution: (
    fromVersion?: number,
  ) => Promise<ClockSettingsDistributionResponse>;
  getEcoModeStats: (fromVersion?: number) => Promise<EcoModeStatsResponse>;
  getMenuNavigationDistribution: (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => Promise<MenuNavigationDistributionResponse>;
  getHeartLongPressStats: (params: {
    days: number;
  }) => Promise<HeartLongPressStatsResponse>;
  getCollectionAnalytics: () => Promise<CollectionAnalyticsResponse>;
  getCollectionPurchaseStats: (
    months?: number,
  ) => Promise<CollectionPurchaseStatsResponse>;
  getCollabpayWidgetData: () => Promise<CollabpayWidgetData>;
  getExploreClocksCount: (days?: number) => Promise<ExploreClocksCountResponse>;
  getClocksWithCollectionCount: (
    collectionId: string,
    betaOnly?: boolean,
  ) => Promise<{ count: number }>;
  getKlydoMonitors: () => Promise<KlydoMonitor[]>;
  getKlydoMonitor: (id: string) => Promise<KlydoMonitor>;
  getAvailableMonitorNames: () => Promise<string[]>;
  createKlydoMonitor: (data: Partial<KlydoMonitor>) => Promise<KlydoMonitor>;
  updateKlydoMonitor: (
    id: string,
    data: Partial<KlydoMonitor>,
  ) => Promise<KlydoMonitor>;
  deleteKlydoMonitor: (id: string) => Promise<void>;
  triggerKlydoMonitor: (id: string, chunkIndex?: number) => Promise<void>;
  snoozeKlydoMonitor: (id: string, durationHours: number) => Promise<void>;
  pauseKlydoMonitor: (id: string) => Promise<void>;
  resumeKlydoMonitor: (id: string) => Promise<void>;
  acknowledgeKlydoMonitor: (id: string) => Promise<void>;
  resolveKlydoMonitor: (id: string) => Promise<void>;
  getClockFieldWatches: () => Promise<ClockFieldWatch[]>;
  createClockFieldWatch: (
    data: Partial<ClockFieldWatch>,
  ) => Promise<ClockFieldWatch>;
  deleteClockFieldWatch: (id: string) => Promise<void>;
  setClockFieldWatchActive: (id: string, active: boolean) => Promise<void>;
  getFeatureBetaGroups: () => Promise<FeatureBetaGroup[]>;
  createFeatureBetaGroup: (
    data: FeatureBetaGroupInput,
  ) => Promise<FeatureBetaGroup>;
  updateFeatureBetaGroup: (
    id: string,
    data: Partial<FeatureBetaGroupInput>,
  ) => Promise<FeatureBetaGroup>;
  deleteFeatureBetaGroup: (id: string) => Promise<void>;
  addClocksToFeatureBetaGroup: (
    id: string,
    clockIds: string[],
  ) => Promise<{ added: number; total: number }>;
  removeClocksFromFeatureBetaGroup: (
    id: string,
    clockIds: string[],
  ) => Promise<{ removed: number; total: number }>;
  getFeatureBetaGroupClocks: (
    id: string,
    params?: { skip?: number; limit?: number; search?: string },
  ) => Promise<{ clocks: ClocksListResponse[]; total: number }>;
  sendFeatureBetaGroupUpdate: (
    id: string,
  ) => Promise<{ sent: number; failed: number }>;
  getContentProfiles: () => Promise<ContentProfileSummary[]>;
  getContentProfile: (id: string) => Promise<ContentProfile>;
  createContentProfile: (data: ContentProfileInput) => Promise<ContentProfile>;
  updateContentProfile: (
    id: string,
    data: Partial<ContentProfileInput>,
  ) => Promise<ContentProfile>;
  deleteContentProfile: (id: string) => Promise<void>;
  markContentProfileReady: (id: string) => Promise<ContentProfile>;
  getClockContentProfiles: (clockId: string) => Promise<ClockContentProfile[]>;
  syncContentProfile: (
    id: string,
    pushDespiteClockMismatch?: boolean,
  ) => Promise<{ sent: number; failed: number }>;
  addClocksToContentProfile: (
    id: string,
    clockIds: string[],
  ) => Promise<{ added: number; total: number }>;
  removeClocksFromContentProfile: (
    id: string,
    clockIds: string[],
  ) => Promise<{ removed: number; total: number }>;
  getContentProfileClocks: (
    id: string,
    params?: { skip?: number; limit?: number; search?: string },
  ) => Promise<{ clocks: ContentProfileClock[]; total: number }>;
  getContentProfileGift: (id: string) => Promise<PopulatedGift | null>;
  upsertContentProfileGift: (
    id: string,
    data: ContentProfileGiftInput,
  ) => Promise<void>;
  deleteContentProfileGift: (id: string) => Promise<void>;
  getKlydoFeedStats: (days: number) => Promise<KlydoFeedStats>;
  getAddCollectionQrStats: (days: number) => Promise<AddCollectionQrStats>;
  getArtistsLandingPageStats: (
    days: number,
  ) => Promise<ArtistsLandingPageStats>;
  getArtistPreviewStats: (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => Promise<ArtistPreviewStats>;
  getSentToClockStats: (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => Promise<SentToClockStats>;
  getAiGreetStats: (days: number) => Promise<AiGreetStats>;
  getKlydoGreetOrderStats: (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => Promise<KlydoGreetOrderStats>;
  getExploreFavoritesStats: (days: number) => Promise<ExploreFavoriteStats[]>;
  getArtistKlydoStats: (userId: string) => Promise<{
    totalKlydos: number;
    totalPublic: number;
    totalTime: number;
    totalFavorites: number;
  }>;
  getIgnoredClockErrors: (version: number) => Promise<IgnoredClockError[]>;
  createIgnoredClockError: (data: {
    errorMessage: string;
    method: string;
    class: string;
    version: number;
    clockId?: string | null;
  }) => Promise<IgnoredClockError>;
  softDeleteIgnoredClockError: (id: string) => Promise<IgnoredClockError>;
  restoreIgnoredClockError: (id: string) => Promise<IgnoredClockError>;
  getUpfrontPayments: (params: {
    from?: string;
    to?: string;
  }) => Promise<UpfrontPayment[]>;
  getAffiliatePayments: (params: {
    from?: string;
    to?: string;
  }) => Promise<AffiliatePayment[]>;
  getRetailers: (refresh?: boolean) => Promise<RetailerSummary[]>;
  getRetailerStats: (params: RetailerStatsParams) => Promise<RetailerStats[]>;
  getRetailerDetail: (
    id: string,
    params: RetailerStatsParams,
  ) => Promise<RetailerDetail>;
  getCollectionProductPageEngagement: (
    days: number,
  ) => Promise<CollectionProductPageEngagementStats>;
  getAffiliateClockStats: (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => Promise<AffiliateClockStats>;
};

/**
 * One upfront ("advance") payment granted to an artist. The advance is posted to
 * CollabPay when the collection is approved, so the entitlement date is the
 * collection's approval date.
 */
export type UpfrontPayment = {
  collectionId: string;
  collectionName: string;
  artistId: string;
  artistName: string;
  /** 'YYYY-MM' */
  entitlementMonth: string;
  /** ISO 8601 approval date. */
  entitlementDate: string;
  upfrontAmount: number;
};

/**
 * Edition ("affiliate") payments earned by an artist, one row per artist ×
 * collection × month. A month's amount is the sum of the per-order payouts the
 * cron sent that month, so it also carries the clock and order counts.
 */
export type AffiliatePayment = {
  collectionId: string;
  collectionName: string;
  artistId: string;
  artistName: string;
  /** 'YYYY-MM' */
  entitlementMonth: string;
  clocks: number;
  orders: number;
  amount: number;
};

/**
 * Retailer stats. Which companies are retailers is set in Shopify (company
 * metafield custom.retailer_type), so there is nothing to write from here.
 * `from` / `to` are inclusive 'YYYY-MM-DD' days on Shopify's order date;
 * `refresh` skips the API's 24 hour caches (companies and clock status).
 */
export type RetailerStatsParams = {
  from?: string;
  to?: string;
  refresh?: boolean;
};

export type RetailerSummary = { id: string; name: string };

export type RetailerStats = RetailerSummary & {
  orders: number;
  clocksSent: number;
  clocksSold: number;
  /** Clocks of these orders whose id we do not have yet, so they cannot be checked. */
  clocksWithoutId: number;
};

export type RetailerDetail = RetailerSummary & {
  orders: number;
  clocksSent: number;
  clocksSold: number;
  clocksOnShelf: number;
  clocksWithoutId: number;
  /** Percent, null when nothing was sent. */
  sellThroughPct: number | null;
  /**
   * Average days from the order to the registration, over the sold clocks that
   * are registered (a clock sold by heartbeat has no sale date). Null when none.
   */
  avgDaysToSale: number | null;
  bySku: { sku: string; sent: number; sold: number }[];
  ordersList: {
    orderName: string;
    /** ISO date */
    orderDate: string;
    clocks: number;
    sold: number;
  }[];
};

export type CollabpayWidgetData = {
  collaboratorsWithPaymentOptions: number;
  collaboratorsWithoutPaymentOptionsButActive: number;
};

export enum MonitorStatus {
  Pending = "pending",
  Running = "running",
  Success = "success",
  Failed = "failed",
}

export enum MonitorType {
  // Server-side monitors executed by the cron scheduler.
  Cron = "cron",
  // Monitors that run on the clocks themselves; clocks report failures.
  // Never executed by the scheduler.
  ClockInternal = "clock-internal",
}

// A single clock failure report, accumulated per incident on clock-internal
// monitors.
export interface ClockMonitorFailureReport {
  clockId: string;
  context: Record<string, unknown>;
  deviceSnapshot: Record<string, unknown> | null;
  reportedAt: number;
}

// One entity the monitor ignores. `target` is whatever the monitor reports as
// the failing entity — an order name, a klydo id, a clock id.
export interface SilencedTarget {
  target: string;
  reason: string | null;
  // Epoch ms after which the silence stops applying; null = until removed.
  until: number | null;
  by: string;
  at: number;
}

export interface KlydoMonitor {
  // --- Base fields (all monitor types) ---
  id: string;
  name: string;
  type: MonitorType;
  status: MonitorStatus;
  // When false, the monitor is disabled: cron monitors are skipped by the
  // scheduler, and failure reports from clocks are ignored entirely. This is
  // what the pause/resume actions toggle.
  active: boolean;
  lastSuccessAt: number | null;
  lastFailedAt: number | null;
  firstFailedAt: number | null;
  lastError: string | null;
  alertPolicyId: string | null;
  // Whether alerts may go out outside working hours (Sun–Thu 09:00–19:00
  // Israel). Defaults to false server-side; absent on older monitors, which
  // means false.
  alertOutsideWorkHours?: boolean;
  // When an off-hours alert was held back; null when nothing is pending.
  alertDeferredAt?: number | null;
  consecutiveFailures: number;
  snoozeUntil: number | null;
  snoozeContext: string | null;
  acknowledge: { by: string; at: number } | null;
  resolved: { by: string; at: number } | null;
  // Entities this monitor ignores: issues attributed to one of these targets
  // never reach the incident or the alert.
  silencedTargets?: SilencedTarget[];
  // The kind of entity this monitor attributes failures to ('order', 'clock',
  // 'klydo'…). Absent for the monitors that report an aggregate — a rate or a
  // count — where there is nothing individual to silence.
  targetType?: string | null;
  // --- Cron-only fields (type === MonitorType.Cron) ---
  chunked?: boolean;
  intervalHours?: number;
  startHour?: number | null;
  chunkSize?: number | null;
  lastRunAt?: number | null;
  runDurationMs?: number | null;
  maxTemporaryErrorsBeforeAlert?: number;
  issues?: string[];
  // Targets of the current incident, after silencing — the mute candidates.
  incidentTargets?: string[];
  // Silenced targets that actually matched on the last run.
  silencedHits?: string[];
  config?: {
    chunkIndex?: number;
    totalChunks?: number;
    [key: string]: unknown;
  };
  // --- Clock-internal-only fields (type === MonitorType.ClockInternal) ---
  // Reports from clocks running a version lower than this are ignored.
  fromVersion?: number | null;
  // Every failure report received during the current incident; cleared on
  // resolve.
  failureReports?: ClockMonitorFailureReport[];
}

export enum WatchMode {
  HeartbeatOnline = "heartbeat-online",
  Change = "change",
}

export enum WatchCollection {
  MachinesReport = "machinesReport",
  Machines = "machines",
}

export interface ClockFieldWatch {
  id: string;
  clockId: string;
  collectionName: WatchCollection;
  fieldPath: string;
  mode: WatchMode;
  // Change-mode only: alert only when the field changes to this exact value.
  targetValue?: unknown | null;
  thresholdMinutes: number;
  notifyTarget: string | null;
  active: boolean;
  lastFiredAt: number | null;
  lastState: Record<string, unknown> | null;
}

// A named set of clocks pinned to a target app version, for rolling a build
// out gradually. Membership only ever raises a clock's target version, so a
// group whose version the public channel has passed is a harmless no-op.
export interface FeatureBetaGroup {
  id: string;
  name: string;
  version: number;
  description: string | null;
  active: boolean;
  // Pins member clocks to exactly `version`, up or down, instead of only
  // raising them. Missing on groups saved before the field existed.
  hardVersion?: boolean;
  createdBy: string | null;
  createdAt?: string;
  updatedAt?: string;
  // The list endpoint returns the count instead of the (potentially huge)
  // clockIds array.
  memberCount: number;
}

export interface FeatureBetaGroupInput {
  name: string;
  version: number;
  description?: string | null;
  active?: boolean;
  hardVersion?: boolean;
}

// A collection/themed-collection entry a profile assigns. Same shape as a
// device's own machineCollections entries, minus addedBy — that gets stamped
// on server-side at sync time, not stored on the profile (see the API's
// ContentProfileCollection comment for why).
export interface ContentProfileCollection {
  id: string;
  permanent?: boolean;
  themed?: boolean;
}

// A named, reusable bundle of clock settings + collections/klydos, assignable
// to a list of devices, with an explicit "Sync" push that overwrites whatever
// was on each assigned device — a profile is a push pipe, not a live mirror.
export interface ContentProfile {
  id: string;
  name: string;
  description: string | null;
  values: Record<string, unknown>;
  machineCollections: ContentProfileCollection[];
  // Klydo ids, in playback order — see ProfileFavoritesCard.
  favorites: string[];
  clockIds: string[];
  // clockId -> ISO timestamp of the last successful sync push to that device.
  pushedAt: Record<string, string>;
  // The target when it is a Shopify order instead of a device list: the
  // API's order cron syncs the order's clocks once it has them and sets
  // submittedAt. No manual Sync or device edits.
  orderName: string | null;
  // Order profiles only: the content-profile-deadline monitor alerts once
  // it passes and the profile still isn't submitted.
  deadline: string | null;
  // Last push to the profile's devices (Sync, or the order trigger).
  submittedAt: string | null;
  // When it was marked ready: a draft (null, also every profile from before
  // the flag) is editable and never pushed; a ready profile is locked.
  readyAt: string | null;
  version: number;
  createdBy: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// The list endpoint's shape — settings/collections/pushedAt are omitted (a
// profile's list row doesn't need them, and clockIds could be large), and
// clockIds itself is collapsed to a count, same convention as
// FeatureBetaGroupSummary. createdByName is resolved server-side from
// createdBy (a Firebase uid) — see AdminContentProfilesController.list().
export type ContentProfileSummary = Omit<
  ContentProfile,
  "values" | "machineCollections" | "pushedAt" | "favorites"
> & {
  deviceCount: number;
  createdByName: string | null;
};

// A profile holding a given clock, as the device modal lists it.
export interface ClockContentProfile {
  id: string;
  name: string;
  orderName: string | null;
  submittedAt: string | null;
  // Last successful push of this profile to the clock.
  pushedAt: string | null;
}

export interface ContentProfileInput {
  name: string;
  description?: string | null;
  values?: Record<string, unknown>;
  machineCollections?: ContentProfileCollection[];
  favorites?: string[];
  orderName?: string | null;
  deadline?: string | null;
}

// A profile's gift lives in its own Mongo document (see GiftService), not on
// the profile itself — delivered device-initiated on the clock's own
// onboarding flow, entirely separate from Sync. One klydo + a greeting, bulk
// linked to every device currently assigned to the profile.
export interface ContentProfileGiftInput {
  klydoId: string;
  greetingContent: string;
  senderName: string;
  senderImageUrl?: string;
}

// pending: never pushed. synced: pushed, and the device has reported a
// heartbeat since. delivered: pushed, but not yet confirmed online since —
// derived server-side on every read, never stored.
export type ContentProfileDeviceStatus = "pending" | "synced" | "delivered";

export type ContentProfileClock = ClocksListResponse & {
  status: ContentProfileDeviceStatus;
};

const adminApiClient: AdminApiClient = {
  saveOnboardingImages: async (images: string[]) => {
    return baseClient.put<void>({
      url: `/admin/internal/studio-onboarding-images`,
      body: { images },
    });
  },
  getOnboardingImages: async () => {
    return baseClient.get<{ images: string[] }>({
      url: `/admin/internal/studio-onboarding-images`,
    });
  },
  getStudioCommits: async (repo: string, since?: string) => {
    const params = new URLSearchParams({ repo });
    if (since) params.append("since", since);
    return baseClient.get<GitHubPull[]>({
      url: `/admin/github/commits?${params.toString()}`,
    });
  },
  getStudioContract: async () => {
    return baseClient.get<{ html: string }>({
      url: `/admin/internal/studio-contract`,
    });
  },
  saveStudioContract: async (html: string) => {
    return baseClient.put<void>({
      url: `/admin/internal/studio-contract`,
      body: { html },
    });
  },
  getStudioFaq: async () => {
    return baseClient.get<{ items: FaqItem[] }>({
      url: `/admin/internal/studio-faq`,
    });
  },
  saveStudioFaq: async (items: FaqItem[]) => {
    return baseClient.put<void>({
      url: `/admin/internal/studio-faq`,
      body: { items },
    });
  },
  getStudioGuides: async () => {
    return baseClient.get<{ items: StudioGuide[] }>({
      url: `/admin/internal/studio-guides`,
    });
  },
  saveStudioGuides: async (items: StudioGuide[]) => {
    return baseClient.put<void>({
      url: `/admin/internal/studio-guides`,
      body: { items },
    });
  },
  getKlydoShareKey: async (klydoId) => {
    return baseClient.get<{ id: string }>({
      url: `/admin/klydos/share-id/${klydoId}`,
    });
  },
  getCollectionShareKey: async (collectionId) => {
    return baseClient.get<{ id: string }>({
      url: `/admin/collections/share-id/${collectionId}`,
    });
  },
  getStudioUsersStats: async () => {
    return baseClient.get<{
      total: number;
      daily: { date: string; count: number }[];
    }>({
      url: `/admin/stats/studio-users-stats`,
    });
  },
  getAllClocksTags: async () => {
    return baseClient.get<string[]>({
      url: `/admin/clocks/metadata/tags`,
    });
  },
  getClockLocations: async () => {
    return baseClient.get<ClockLocations>({
      url: `/admin/clocks/metadata/locations`,
    });
  },
  getNumberOfArtistsCreatedKlydos: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/number-of-artists-created-klydos`,
    });
  },
  getNumberOfPendingPublicationRequests: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/number-of-publication-requests-pending`,
    });
  },
  getNumberOfCollectionsInReview: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/collection/number-of-collections-in-review`,
    });
  },

  getNumberOfCollectionsInMarketplace: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/collection/number-of-collections-in-marketplace`,
    });
  },
  getNumberOfCollectionsWaitingForPublish: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/collection/number-of-collections-waiting-for-publish`,
    });
  },
  getNumberOfArtists: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/number-of-artists`,
    });
  },
  getNumberOfArtistsClocks: async () => {
    return baseClient.get<number>({
      url: `/admin/stats/number-of-artists-clocks`,
    });
  },
  updateUser: async (userId, user) => {
    return baseClient.put<User>({
      url: `/admin/users/${userId}`,
      body: user,
    });
  },
  updateClockMetadata: async (clockId, metadata) => {
    return baseClient.put<ClockMetadata>({
      url: `/admin/clocks/metadata/${clockId}`,
      body: metadata,
    });
  },
  getClockMetadata: async (clockId) => {
    return baseClient.get<ClockMetadata>({
      url: `/admin/clocks/metadata/${clockId}`,
    });
  },
  deleteUser: async (userId, options) => {
    // baseRequest.ts is a protected file (cannot be modified via PR) and its
    // shared `delete` wrapper doesn't forward a request body, so issue this one
    // call directly to send the deletion options.
    const res = await axios.delete<DeleteUserResult>(
      `${baseUrl}/admin/users/${userId}`,
      {
        headers: { Authorization: `Bearer ${await getToken()}` },
        data: options ?? {},
      },
    );
    return res.data;
  },
  getUserContent: async (userId) => {
    return baseClient.get<UserContent>({
      url: `/admin/users/${userId}/content`,
    });
  },
  getOrderClock: async (orderName) => {
    return baseClient.get<Device[]>({
      url: `/admin/clocks/order/${orderName}`,
    });
  },
  createUser: async (user) =>
    baseClient.post<User>({
      url: "/admin/users/create",
      body: user,
    }),
  createUserCollaborator: async (userId) =>
    baseClient.post<{ collaboratorId: string }>({
      url: `/admin/users/${userId}/collaborator`,
    }),
  resetPassword: async (userId) => {
    return baseClient.post<{ link: string }>({
      url: `/admin/users/${userId}/reset-password`,
    });
  },
  setUserPassword: async (userId, password) => {
    return baseClient.post<void>({
      url: `/admin/users/${userId}/set-password`,
      body: { password },
    });
  },
  setUserEmailVerified: async (userId) => {
    return baseClient.post<void>({
      url: `/admin/users/${userId}/verify-email`,
      body: { emailVerified: true },
    });
  },
  getArtistKlydoStats: async (userId: string) => {
    return baseClient.get<{
      totalKlydos: number;
      totalPublic: number;
      totalTime: number;
      totalFavorites: number;
    }>({
      url: `/admin/users/${userId}/klydo-stats`,
    });
  },
  publishVersion: async (versionUpdate) => {
    return baseClient.post<void>({
      url: `/admin/publish-version`,
      body: versionUpdate,
    });
  },
  getClocks: async (input: GetClocksInput) => {
    const {
      skip,
      limit,
      query,
      devClocks,
      betaClocks,
      registrationStatus,
      fromVersion,
      toVersion,
      heartbeatMinutes,
      artistClocks,
      country,
      city,
      region,
      timeZone,
      mode,
      cycle,
      settings,
      ids,
      monitorId,
      featureBetaGroupId,
      fetchAll,
      sortBy,
      sortDir,
    } = input;

    const params = new URLSearchParams();
    params.append("skip", skip.toString());
    params.append("limit", limit.toString());

    if (query) params.append("search", query);
    if (devClocks !== undefined) params.append("dev", devClocks.toString());
    if (betaClocks !== undefined) params.append("beta", betaClocks.toString());
    if (registrationStatus)
      params.append("registrationStatus", registrationStatus);
    if (fromVersion !== undefined)
      params.append("fromVersion", fromVersion.toString());
    if (toVersion !== undefined)
      params.append("toVersion", toVersion.toString());
    if (heartbeatMinutes !== undefined)
      params.append("heartbeatMinutes", heartbeatMinutes.toString());
    if (artistClocks !== undefined)
      params.append("artist", artistClocks.toString());
    if (country) params.append("country", country);
    if (city) params.append("city", city);
    if (region) params.append("region", region);
    if (timeZone) params.append("timeZone", timeZone);
    if (mode) params.append("mode", mode);
    if (cycle) params.append("cycle", cycle);
    if (settings?.length) params.append("settings", settings.join(","));
    if (ids?.length) params.append("ids", ids.join(","));
    if (monitorId) params.append("monitorId", monitorId);
    if (featureBetaGroupId)
      params.append("featureBetaGroupId", featureBetaGroupId);
    if (fetchAll) params.append("fetchAll", "true");
    if (sortBy) params.append("sortBy", sortBy);
    if (sortDir) params.append("sortDir", sortDir);

    return baseClient.get<ClocksListResponse[]>({
      url: `/admin/clocks?${params.toString()}`,
    });
  },
  getClock: async (id: string) => {
    return baseClient.get<Device>({
      url: `/admin/clocks/${id}`,
    });
  },
  getClocksRemindersSummary: async () => {
    return baseClient.get<ClockReminderSummaryEntry[]>({
      url: `/admin/clocks/reminders-summary`,
    });
  },

  getClocksErrors: async (input: GetClocksErrorsInput) => {
    const { fromVersion, toVersion, skip = 0, limit = 500 } = input;
    return baseClient.get<ClockErrorsResponse>({
      url: `/admin/stats/clocks-errors`,
      params: {
        fromVersion,
        toVersion,
        skip,
        limit,
      },
    });
  },

  getMonthlyRegisteredDevices: async (monthLimit: number) => {
    return baseClient.get<MonthlyRegisteredDevicesResponse[]>({
      url: `/admin/stats/get-monthly-registered-clocks?monthLimit=${monthLimit}`,
    });
  },
  searchUsers: (input: SearchUserInput) =>
    baseClient.post<SearchUserOutput[]>({
      url: "/admin/users/.search",
      body: input,
    }),
  getUserById: (userId: string) =>
    baseClient.get<User>({
      url: `/admin/users/${userId}`,
    }),
  getUserProperties: (userId: string) =>
    baseClient.get<UserProperties>({
      url: `/admin/users/${userId}/properties`,
    }),
  setUserProperties: (userId: string, b2b: boolean) =>
    baseClient.post<{ b2b: boolean }>({
      url: `/admin/users/${userId}/properties`,
      body: { b2b },
    }),
  getClocksByTags: (
    tags: string[],
    includeNonRegistered?: boolean,
    dev?: boolean,
  ) =>
    baseClient.post<Device[]>({
      url: "/admin/clocks/search-by-tags",
      body: { tags, includeNonRegistered, dev },
    }),
  getGreetCategoriesStats: async () => {
    return baseClient.get<GreetCategoryStats[]>({
      url: "/admin/stats/greet-categories",
    });
  },
  getGreetCategoryKlydoStats: async (category: string) => {
    return baseClient.get<GreetCategoryKlydoStats[]>({
      url: `/admin/stats/greet-categories/${encodeURIComponent(category)}`,
    });
  },
  getClockChangeLogs: async (
    clockId: string,
    limit: number,
    fields: FieldChangeFilter,
    fromDate?: string,
  ) => {
    return baseClient.post<FieldChangeRow[]>({
      url: `/admin/clocks/get-clock-change-log`,
      body: {
        clockId,
        limit,
        fields,
        fromDate,
      },
    });
  },
  getMapDevices: async (bounds: MapBounds) => {
    return await baseClient.post<MapDevice[]>({
      url: `/admin/clocks/map-devices`,
      body: {
        northEastLat: bounds.northEast.lat,
        northEastLng: bounds.northEast.lng,
        southWestLat: bounds.southWest.lat,
        southWestLng: bounds.southWest.lng,
      },
    });
  },
  getMapCollections: async (bounds: MapBounds) => {
    return await baseClient.post<MapCollection[]>({
      url: `/admin/clocks/map-collections`,
      body: {
        northEastLat: bounds.northEast.lat,
        northEastLng: bounds.northEast.lng,
        southWestLat: bounds.southWest.lat,
        southWestLng: bounds.southWest.lng,
      },
    });
  },
  getTopCollections: async (bounds: MapBounds, limit: number) => {
    return await baseClient.post<TopCollectionsResponse>({
      url: `/admin/stats/geo-top-collections`,
      body: {
        limit,
        northEastLat: bounds.northEast.lat,
        northEastLng: bounds.northEast.lng,
        southWestLat: bounds.southWest.lat,
        southWestLng: bounds.southWest.lng,
      },
    });
  },
  getTopKlydos: async (bounds: MapBounds, limit: number) => {
    return await baseClient.post<TopKlydosResponse>({
      url: `/admin/stats/geo-top-klydos`,
      body: {
        limit,
        northEastLat: bounds.northEast.lat,
        northEastLng: bounds.northEast.lng,
        southWestLat: bounds.southWest.lat,
        southWestLng: bounds.southWest.lng,
      },
    });
  },

  addClocksToUser: async (userId: string, clockIds: string[]) => {
    return baseClient.post<{ user: User; token: string }>({
      url: `/users/${userId}/add-clocks`,
      body: { clockIds },
    });
  },

  getUserClocks: async (userId: string) => {
    return baseClient.get<string[]>({
      url: `/users/${userId}/clocks`,
    });
  },

  removeClocksFromUser: async (userId: string, clockId: string) => {
    return baseClient.post<{ user: User; token: string }>({
      url: `/users/${userId}/remove-clocks`,
      body: { clockId },
    });
  },

  getGroupedClockErrors: async () => {
    return baseClient.get<GroupedClockError[]>({
      url: `/admin/stats/clocks-errors-grouped`,
    });
  },

  deleteClockError: async (errorGroup) => {
    return baseClient.post<void>({
      url: `/admin/stats/clocks-errors-delete`,
      body: errorGroup,
    });
  },

  getUnregisteredDevicesCount: async () => {
    return baseClient.get<{ count: number }>({
      url: `/admin/stats/unregistered-devices`,
    });
  },

  getLastVersionNumber: async (clockId: string) => {
    const res = await baseClient.get<{ v: number } | string>({
      url: `/admin/clocks/${clockId}/last-version-number`,
    });

    if (typeof res === "string") return res;
    return res.v;
  },

  signOutUser: async (userId: string) => {
    return baseClient.post<void>({
      url: `/admin/users/${userId}/sign-out`,
    });
  },
  getClockOnlineLogs: async (clockId: string, dateLimit: string) => {
    return baseClient.post<ClockOnlineLog[]>({
      url: `/admin/clocks/get-clock-online-logs`,
      body: { clockId, dateLimit },
    });
  },
  getDeviceErrorLogs: async (
    clockId: string,
    period: string,
    severity: DeviceLogSeverity,
  ) => {
    return baseClient.post<DeviceErrorLog[]>({
      url: `/admin/clocks/get-device-error-logs`,
      body: { clockId, period, severity },
    });
  },

  clearGift: async (clockId: string) => {
    return baseClient.post<void>({
      url: `/admin/clocks/${clockId}/clear-gift`,
    });
  },

  clearOrder: async (clockId: string) => {
    return baseClient.post<void>({
      url: `/admin/clocks/${clockId}/clear-order`,
    });
  },

  detachOrder: async (clockId: string, orderName: string) => {
    return baseClient.post<void>({
      url: `/admin/clocks/${clockId}/detach-order`,
      body: { orderName },
    });
  },

  transferDeviceData: async (
    clockId: string,
    params: TransferDeviceDataParams,
  ) => {
    return baseClient.post<TransferDeviceDataResult>({
      url: `/admin/clocks/${clockId}/transfer`,
      body: {
        targetClockId: params.targetClockId,
        overwrite: params.overwrite ?? false,
        transferData: params.transferData ?? true,
        keepSourceData: params.keepSourceData ?? false,
        transferCollections: params.transferCollections ?? true,
      },
    });
  },

  getKlydoStats: async () => {
    return baseClient.get<KlydoStatsResponse>({
      url: `/admin/stats/klydo-stats`,
    });
  },

  getKlydoDeleteCount: async (klydoId: string) => {
    return baseClient.get<{ deleted: number }>({
      url: `/admin/stats/klydo-delete-count/${klydoId}`,
    });
  },

  getClockSettingsDistribution: async (fromVersion?: number) => {
    const params = fromVersion != null ? `?fromVersion=${fromVersion}` : "";
    return baseClient.get<ClockSettingsDistributionResponse>({
      url: `/admin/stats/clock-settings-distribution${params}`,
    });
  },
  getEcoModeStats: async (fromVersion?: number) => {
    return baseClient.get<EcoModeStatsResponse>({
      url: `/admin/stats/eco-mode-stats`,
      params: fromVersion != null ? { fromVersion } : undefined,
    });
  },
  getMenuNavigationDistribution: async (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => {
    return baseClient.get<MenuNavigationDistributionResponse>({
      url: `/admin/stats/menu-navigation-distribution`,
      params,
    });
  },
  getHeartLongPressStats: async (params: { days: number }) => {
    return baseClient.get<HeartLongPressStatsResponse>({
      url: `/admin/stats/heart-long-press-stats`,
      params,
    });
  },
  getCollectionAnalytics: async () => {
    return baseClient.get<CollectionAnalyticsResponse>({
      url: `/admin/stats/collection/collection-analytics`,
    });
  },
  getCollectionPurchaseStats: async (months: number = 12) => {
    return baseClient.get<CollectionPurchaseStatsResponse>({
      url: `/admin/stats/collection/purchase-stats`,
      params: { months },
    });
  },
  getCollabpayWidgetData: async () => {
    return baseClient.get<CollabpayWidgetData>({
      url: `/admin/stats/payments-widget-data`,
    });
  },
  getExploreClocksCount: async (days: number = 1) => {
    return baseClient.get<ExploreClocksCountResponse>({
      url: `/admin/stats/explore-clocks-count`,
      params: { days },
    });
  },
  getClocksWithCollectionCount: async (
    collectionId: string,
    betaOnly: boolean = false,
  ) => {
    return baseClient.get<{ count: number }>({
      url: `/admin/stats/clocks-with-collection-count`,
      params: betaOnly ? { collectionId, beta: true } : { collectionId },
    });
  },

  getKlydoMonitors: async () => {
    return baseClient.get<KlydoMonitor[]>({ url: "/admin/monitors" });
  },

  getKlydoMonitor: async (id: string) => {
    return baseClient.get<KlydoMonitor>({ url: `/admin/monitors/${id}` });
  },

  getAvailableMonitorNames: async () => {
    return baseClient.get<string[]>({ url: "/admin/monitors/available" });
  },

  createKlydoMonitor: async (data: Partial<KlydoMonitor>) => {
    return baseClient.post<KlydoMonitor>({
      url: "/admin/monitors",
      body: data,
    });
  },

  updateKlydoMonitor: async (id: string, data: Partial<KlydoMonitor>) => {
    return baseClient.put<KlydoMonitor>({
      url: `/admin/monitors/${id}`,
      body: data,
    });
  },

  deleteKlydoMonitor: async (id: string) => {
    return baseClient.delete<void>({ url: `/admin/monitors/${id}` });
  },

  getClockFieldWatches: async () => {
    return baseClient.get<ClockFieldWatch[]>({ url: "/admin/clock-watches" });
  },

  createClockFieldWatch: async (data: Partial<ClockFieldWatch>) => {
    return baseClient.post<ClockFieldWatch>({
      url: "/admin/clock-watches",
      body: data,
    });
  },

  deleteClockFieldWatch: async (id: string) => {
    return baseClient.delete<void>({ url: `/admin/clock-watches/${id}` });
  },

  setClockFieldWatchActive: async (id: string, active: boolean) => {
    return baseClient.post<void>({
      url: `/admin/clock-watches/${id}/active`,
      body: { active },
    });
  },

  getFeatureBetaGroups: async () => {
    return baseClient.get<FeatureBetaGroup[]>({
      url: "/admin/feature-beta-groups",
    });
  },

  createFeatureBetaGroup: async (data: FeatureBetaGroupInput) => {
    return baseClient.post<FeatureBetaGroup>({
      url: "/admin/feature-beta-groups",
      body: data,
    });
  },

  updateFeatureBetaGroup: async (
    id: string,
    data: Partial<FeatureBetaGroupInput>,
  ) => {
    return baseClient.put<FeatureBetaGroup>({
      url: `/admin/feature-beta-groups/${id}`,
      body: data,
    });
  },

  deleteFeatureBetaGroup: async (id: string) => {
    return baseClient.delete<void>({
      url: `/admin/feature-beta-groups/${id}`,
    });
  },

  addClocksToFeatureBetaGroup: async (id: string, clockIds: string[]) => {
    return baseClient.post<{ added: number; total: number }>({
      url: `/admin/feature-beta-groups/${id}/clocks`,
      body: { clockIds },
    });
  },

  // A POST, not a DELETE: `baseClient.delete` sends no body and a select-all
  // removal can carry thousands of ids.
  removeClocksFromFeatureBetaGroup: async (id: string, clockIds: string[]) => {
    return baseClient.post<{ removed: number; total: number }>({
      url: `/admin/feature-beta-groups/${id}/remove-clocks`,
      body: { clockIds },
    });
  },

  getFeatureBetaGroupClocks: async (
    id: string,
    params?: { skip?: number; limit?: number; search?: string },
  ) => {
    return baseClient.get<{ clocks: ClocksListResponse[]; total: number }>({
      url: `/admin/feature-beta-groups/${id}/clocks`,
      params,
    });
  },

  sendFeatureBetaGroupUpdate: async (id: string) => {
    return baseClient.post<{ sent: number; failed: number }>({
      url: `/admin/feature-beta-groups/${id}/send-update`,
    });
  },

  getContentProfiles: async () => {
    return baseClient.get<ContentProfileSummary[]>({
      url: "/admin/content-profiles",
    });
  },

  getContentProfile: async (id: string) => {
    return baseClient.get<ContentProfile>({
      url: `/admin/content-profiles/${id}`,
    });
  },

  createContentProfile: async (data: ContentProfileInput) => {
    return baseClient.post<ContentProfile>({
      url: "/admin/content-profiles",
      body: data,
    });
  },

  updateContentProfile: async (
    id: string,
    data: Partial<ContentProfileInput>,
  ) => {
    return baseClient.put<ContentProfile>({
      url: `/admin/content-profiles/${id}`,
      body: data,
    });
  },

  deleteContentProfile: async (id: string) => {
    return baseClient.delete<void>({
      url: `/admin/content-profiles/${id}`,
    });
  },

  getClockContentProfiles: async (clockId: string) => {
    return baseClient.get<ClockContentProfile[]>({
      url: `/admin/content-profiles/by-clock/${clockId}`,
    });
  },

  markContentProfileReady: async (id: string) => {
    return baseClient.post<ContentProfile>({
      url: `/admin/content-profiles/${id}/ready`,
    });
  },

  syncContentProfile: async (id: string, pushDespiteClockMismatch) => {
    return baseClient.post<{ sent: number; failed: number }>({
      url: `/admin/content-profiles/${id}/sync`,
      body: { pushDespiteClockMismatch },
    });
  },

  addClocksToContentProfile: async (id: string, clockIds: string[]) => {
    return baseClient.post<{ added: number; total: number }>({
      url: `/admin/content-profiles/${id}/clocks`,
      body: { clockIds },
    });
  },

  // A POST, not a DELETE: `baseClient.delete` sends no body and a select-all
  // removal can carry thousands of ids.
  removeClocksFromContentProfile: async (id: string, clockIds: string[]) => {
    return baseClient.post<{ removed: number; total: number }>({
      url: `/admin/content-profiles/${id}/remove-clocks`,
      body: { clockIds },
    });
  },

  getContentProfileClocks: async (
    id: string,
    params?: { skip?: number; limit?: number; search?: string },
  ) => {
    return baseClient.get<{ clocks: ContentProfileClock[]; total: number }>({
      url: `/admin/content-profiles/${id}/clocks`,
      params,
    });
  },

  getContentProfileGift: async (id: string) => {
    return baseClient.get<PopulatedGift | null>({
      url: `/admin/content-profiles/${id}/gift`,
    });
  },

  upsertContentProfileGift: async (
    id: string,
    data: ContentProfileGiftInput,
  ) => {
    return baseClient.put<void>({
      url: `/admin/content-profiles/${id}/gift`,
      body: data,
    });
  },

  deleteContentProfileGift: async (id: string) => {
    return baseClient.delete<void>({
      url: `/admin/content-profiles/${id}/gift`,
    });
  },

  triggerKlydoMonitor: async (id: string, chunkIndex?: number) => {
    return baseClient.post<void>({
      url: `/admin/monitors/${id}/trigger`,
      body: chunkIndex === undefined ? {} : { chunkIndex },
    });
  },

  snoozeKlydoMonitor: async (id: string, durationHours: number) => {
    return baseClient.post<void>({
      url: `/admin/monitors/${id}/snooze`,
      body: { durationHours },
    });
  },
  pauseKlydoMonitor: async (id: string) => {
    return baseClient.post<void>({
      url: `/admin/monitors/${id}/pause`,
      body: {},
    });
  },
  resumeKlydoMonitor: async (id: string) => {
    return baseClient.post<void>({
      url: `/admin/monitors/${id}/resume`,
      body: {},
    });
  },
  acknowledgeKlydoMonitor: async (id: string) => {
    return baseClient.post<void>({
      url: `/admin/monitors/${id}/acknowledge`,
      body: {},
    });
  },
  resolveKlydoMonitor: async (id: string) => {
    return baseClient.post<void>({
      url: `/admin/monitors/${id}/resolve`,
      body: {},
    });
  },
  getKlydoFeedStats: async (days: number) => {
    return baseClient.get<KlydoFeedStats>({
      url: `/admin/stats/klydo-feed-stats`,
      params: { days },
    });
  },
  getAddCollectionQrStats: async (days: number) => {
    return baseClient.get<AddCollectionQrStats>({
      url: `/admin/stats/add-collection-qr-stats`,
      params: { days },
    });
  },
  getArtistsLandingPageStats: async (days: number) => {
    return baseClient.get<ArtistsLandingPageStats>({
      url: `/admin/stats/artists-landing-page-stats`,
      params: { days },
    });
  },
  getArtistPreviewStats: async (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => {
    return baseClient.get<ArtistPreviewStats>({
      url: `/admin/stats/artist-preview-stats`,
      params,
    });
  },
  getSentToClockStats: async (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => {
    return baseClient.get<SentToClockStats>({
      url: `/admin/stats/sent-to-clock-stats`,
      params,
    });
  },
  getKlydoGreetOrderStats: async (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => {
    return baseClient.get<KlydoGreetOrderStats>({
      url: `/admin/stats/klydogreet-orders`,
      params,
    });
  },
  getAiGreetStats: async (days: number) => {
    return baseClient.get<AiGreetStats>({
      url: `/admin/stats/ai-greet-stats`,
      params: { days },
    });
  },
  getExploreFavoritesStats: async (days: number) => {
    return baseClient.get<ExploreFavoriteStats[]>({
      url: `/admin/stats/explore-favorites`,
      params: { days },
    });
  },
  getIgnoredClockErrors: async (version: number) => {
    return baseClient.get<IgnoredClockError[]>({
      url: `/admin/monitors/ignored-clock-errors`,
      params: { version },
    });
  },
  createIgnoredClockError: async (data) => {
    return baseClient.post<IgnoredClockError>({
      url: `/admin/monitors/ignored-clock-errors`,
      body: data,
    });
  },
  softDeleteIgnoredClockError: async (id: string) => {
    return baseClient.put<IgnoredClockError>({
      url: `/admin/monitors/ignored-clock-errors/${id}/soft-delete`,
    });
  },
  restoreIgnoredClockError: async (id: string) => {
    return baseClient.put<IgnoredClockError>({
      url: `/admin/monitors/ignored-clock-errors/${id}/restore`,
    });
  },
  getUpfrontPayments: async (params: { from?: string; to?: string }) => {
    return baseClient.get<UpfrontPayment[]>({
      url: `/admin/stats/collection/upfront-payments`,
      params,
    });
  },
  getAffiliatePayments: async (params: { from?: string; to?: string }) => {
    return baseClient.get<AffiliatePayment[]>({
      url: `/admin/stats/collection/affiliate-payments`,
      params,
    });
  },
  getRetailers: async (refresh?: boolean) => {
    return baseClient.get<RetailerSummary[]>({
      url: `/admin/retailers`,
      params: { refresh },
    });
  },
  getRetailerStats: async (params: RetailerStatsParams) => {
    return baseClient.get<RetailerStats[]>({
      url: `/admin/retailers/stats`,
      params,
    });
  },
  getRetailerDetail: async (id: string, params: RetailerStatsParams) => {
    return baseClient.get<RetailerDetail>({
      url: `/admin/retailers/${encodeURIComponent(id)}/stats`,
      params,
    });
  },
  getCollectionProductPageEngagement: async (days: number) => {
    return baseClient.get<CollectionProductPageEngagementStats>({
      url: `/admin/stats/collection-product-page-engagement`,
      params: { days },
    });
  },
  getAffiliateClockStats: async (params: {
    days?: number;
    from?: string;
    to?: string;
  }) => {
    return baseClient.get<AffiliateClockStats>({
      url: `/admin/stats/affiliate-clock-stats`,
      params,
    });
  },
};
export default adminApiClient;
